
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowLeft, Award, BarChart2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import { fetchCaseStudies, type CaseStudy } from "@/services/caseStudyService";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

const CaseStudyDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [caseStudy, setCaseStudy] = useState<CaseStudy | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCaseStudy = async () => {
      try {
        setLoading(true);
        const allCaseStudies = await fetchCaseStudies();
        const study = allCaseStudies.find(study => study.id === id);
        
        if (study) {
          setCaseStudy(study);
        } else {
          toast.error("Case study not found");
        }
      } catch (error) {
        console.error("Failed to load case study:", error);
        toast.error("Failed to load case study details");
      } finally {
        setLoading(false);
      }
    };

    loadCaseStudy();
  }, [id]);

  if (loading) {
    return (
      <PageTransition>
        <Navbar />
        <div className="page-container min-h-[70vh] flex items-center justify-center">
          <div className="text-center">
            <div className="h-20 w-20 rounded-full border-4 border-t-primary animate-spin mx-auto mb-4"></div>
            <p className="text-foreground/70">Loading case study...</p>
          </div>
        </div>
        <Footer />
      </PageTransition>
    );
  }

  if (!caseStudy) {
    return (
      <PageTransition>
        <Navbar />
        <div className="page-container min-h-[70vh] flex items-center justify-center">
          <div className="text-center max-w-lg mx-auto py-16">
            <h1 className="text-4xl font-bold text-desert-sunset mb-4">Case Study Not Found</h1>
            <p className="text-foreground/70 mb-8">
              The case study you're looking for doesn't exist or has been removed.
            </p>
            <Button asChild className="bg-desert-gradient hover:opacity-90">
              <Link to="/case-studies" className="flex items-center">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Case Studies
              </Link>
            </Button>
          </div>
        </div>
        <Footer />
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <Helmet>
        <title>{caseStudy.title} | DesertSEO Case Study</title>
        <meta name="description" content={caseStudy.description} />
      </Helmet>

      <Navbar />

      <div className="bg-gradient-to-b from-muted/50 to-background pt-20 pb-10">
        <div className="page-container">
          <Link to="/case-studies" className="inline-flex items-center text-primary hover:underline mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Case Studies
          </Link>

          {caseStudy.featured && (
            <div className="inline-flex items-center rounded-full bg-primary/90 text-white text-sm px-3 py-1 mb-4 ml-2">
              <Award className="w-4 h-4 mr-1" />
              <span>Featured Case Study</span>
            </div>
          )}

          <h1 className="text-4xl md:text-5xl font-bold mb-4">{caseStudy.title}</h1>
          
          <div className="inline-block px-3 py-1 bg-muted rounded-full text-sm font-medium mb-8">
            {caseStudy.category}
          </div>
        </div>
      </div>

      <div className="page-container py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="rounded-xl overflow-hidden mb-8">
              <img src={caseStudy.image} alt={caseStudy.title} className="w-full h-auto" />
            </div>

            <div className="prose prose-lg max-w-none">
              <h2>Challenge</h2>
              <p>{caseStudy.description}</p>
              
              <h2>Our Approach</h2>
              <p>
                For {caseStudy.title}, we developed a customized SEO strategy that addressed their 
                specific challenges in the {caseStudy.category} space. Our comprehensive approach included:
              </p>
              <ul>
                <li>In-depth keyword research and market analysis</li>
                <li>Technical SEO audit and optimization</li>
                <li>Content strategy development and implementation</li>
                <li>Local SEO optimization for Los Angeles area</li>
                <li>Ongoing performance tracking and strategy refinement</li>
              </ul>
              
              <h2>Results</h2>
              <p>
                Through our targeted approach, we were able to achieve significant improvements 
                in {caseStudy.title}'s online visibility and business performance:
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-8">
              {caseStudy.results.map((result, index) => (
                <div key={index} className="bg-muted rounded-lg p-4">
                  <div className="text-2xl font-bold">{result.value}</div>
                  {result.change && (
                    <div className={result.positive ? "text-green-500" : "text-red-500"}>
                      {result.positive ? "+" : ""}{result.change}
                    </div>
                  )}
                  <div className="text-sm text-foreground/70">{result.label}</div>
                </div>
              ))}
            </div>
            
            <div className="prose prose-lg max-w-none">
              <h2>Conclusion</h2>
              <p>
                This case study demonstrates how our data-driven SEO approach can transform 
                online performance for businesses in the {caseStudy.category} sector. By focusing 
                on both technical excellence and strategic content development, we were able to 
                deliver measurable results that directly impacted the client's bottom line.
              </p>
            </div>
          </div>
          
          <div className="lg:col-span-1">
            <div className="bg-muted rounded-xl p-6 sticky top-24">
              <h3 className="text-xl font-bold mb-4">Project Overview</h3>
              
              <div className="mb-4">
                <div className="text-sm text-foreground/70 mb-1">Industry</div>
                <div className="font-medium">{caseStudy.category}</div>
              </div>
              
              <div className="mb-4">
                <div className="text-sm text-foreground/70 mb-1">Services Provided</div>
                <div className="font-medium">
                  SEO Strategy, Technical SEO, Content Optimization
                </div>
              </div>
              
              <div className="mb-4">
                <div className="text-sm text-foreground/70 mb-1">Project Duration</div>
                <div className="font-medium">6 Months</div>
              </div>
              
              <Separator className="my-6" />
              
              <h3 className="text-xl font-bold mb-4">Key Achievements</h3>
              
              {caseStudy.results.map((result, index) => (
                <div key={index} className="flex items-start mb-3">
                  <BarChart2 className="w-5 h-5 text-primary mr-2 mt-1" />
                  <div>
                    <div className="font-medium">{result.label}</div>
                    <div className="text-sm text-foreground/70">
                      {result.value} {result.change && `(${result.positive ? "+" : ""}${result.change})`}
                    </div>
                  </div>
                </div>
              ))}
              
              <Separator className="my-6" />
              
              <div className="text-center">
                <p className="text-sm text-foreground/70 mb-4">
                  Ready to achieve similar results for your business?
                </p>
                <Button asChild className="bg-desert-gradient hover:opacity-90 w-full">
                  <Link to="/contact">Contact Us Today</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </PageTransition>
  );
};

export default CaseStudyDetail;
